import { logger } from '@gik/analytics/utils/logger';
import { swrDefaults } from '@gik/core/api/swr/swrDefaults';
import { mapActivityToUserUpdate } from '@gik/inkind-page/components/InkindUserUpdates/InkindUserUpdates';
import type InkindUserUpdate from '@gik/inkind-page/models/InkindUserUpdate';
import type {
  FlatActivity,
  GetFeedOptions,
  PostActivity,
  Reaction,
  StreamComment,
  UserUpdateActivity,
} from '@gik/stream/types/Stream';
import { FeedType } from '@gik/stream/types/Stream';
import StreamClient from '@gik/stream/utils/StreamClient';
import type { UR } from 'getstream';
import React from 'react';
import useSWR from 'swr';
export interface IStreamConfig {
  streamUserToken: string;
  streamApiKey: string;
  streamAppId: string;
}

export type ReactionLookupAttribute = 'activity' | 'reaction' | 'user';

// TODO: rename UseStreamSWROptions to GetFeedOptions
export type UseStreamSWROptions = GetFeedOptions;

export function useUserUpdates(inkindRouteId: string, options?: UseStreamSWROptions) {
  return useSWR<UserUpdateActivity[]>(
    inkindRouteId ? getSWRUrl(FeedType.UserUpdates, inkindRouteId, options) : null,
    streamFeedFetcher,
    {
      ...swrDefaults,
    }
  );
}

export function useUserUpdatesTransformed(inkindRouteId: string, options?: UseStreamSWROptions) {
  const [userUpdates, setUserUpdates] = React.useState<InkindUserUpdate[]>();

  // function updatesSort(a: InkindUserUpdate, b: InkindUserUpdate) {
  //   const da = moment(a.publishedDate, DateTimeUtils.DateFormat);
  //   const db = moment(b.publishedDate, DateTimeUtils.DateFormat);

  //   return da.isAfter(db) ? -1 : da.isBefore(db) ? 1 : 0;
  // }

  const { data: updates, mutate, isValidating } = useUserUpdates(inkindRouteId, options);

  React.useEffect(() => {
    if (isValidating || !Array.isArray(updates)) return;
    const _userUpdates = updates
      ?.filter(activity => activity.userUpdate && !activity.userUpdateDeleted && !activity.deletedTime)
      .map(mapActivityToUserUpdate);
    // .sort(updatesSort);

    setUserUpdates(_userUpdates);
  }, [updates, isValidating]);

  return { data: updates, transformed: userUpdates, mutate, isValidating };
}

export function useStreamPost(id: string) {
  return useSWR<PostActivity>(id ? getSWRUrl(FeedType.Posts, id) : null, streamPostFetcher, {
    ...swrDefaults,
  });
}

export function useStreamPosts(inkindRouteId: string, options?: UseStreamSWROptions) {
  return useSWR<PostActivity[]>(
    inkindRouteId ? getSWRUrl(FeedType.Posts, inkindRouteId, options) : null,
    streamFeedFetcher,
    {
      ...swrDefaults,
    }
  );
}

export function useStreamPostFeed(inkindRouteId: string, options?: UseStreamSWROptions) {
  return useSWR<PostActivity[]>(
    inkindRouteId ? getSWRUrl(FeedType.Posts, inkindRouteId, options) : null,
    (url: string) =>
      streamFeedFetcher(url, {
        withOwnChildren: false,
        withOwnReactions: false,
        withReactionCounts: false,
        withRecentReactions: false,
        ownReactions: false,
        enrich: false,
      }),
    {
      ...swrDefaults,
    }
  );
}

export function useComment(id?: string) {
  return useSWR<Reaction<StreamComment>>(id ? getSWRUrl('reaction', id) : null, streamReactionFetcher, {
    ...swrDefaults,
  });
}

export function useComments(parentType: ReactionLookupAttribute, parentId?: string, options?: UseStreamSWROptions) {
  return useSWR<Reaction<StreamComment>[]>(
    parentId ? getSWRUrl(parentType, parentId, { ...options }) : null,
    streamCommentsFetcher,
    {
      ...swrDefaults,
    }
  );
}

export function useReactions(parentType: ReactionLookupAttribute, parentId?: string, options?: UseStreamSWROptions) {
  return useSWR<Reaction<UR>[]>(
    parentId ? getSWRUrl(parentType, parentId, { ...options }) : null,
    streamReactionsFetcher,
    {
      ...swrDefaults,
    }
  );
}

function getSWRUrl(feedOrAttributeType: string, feedOrAttributeId: string, options: UseStreamSWROptions = {}) {
  if (!feedOrAttributeType) return null;
  return `${feedOrAttributeType}/${feedOrAttributeId}?${JSON.stringify(options)}`;
}

async function streamFeedFetcher<T extends FlatActivity>(url: string, getOptions: GetFeedOptions = {}) {
  try {
    const feed = url.split('/').shift() as FeedType;
    const inkindRouteId = url.split('/').pop().split('?').shift();
    const options: UseStreamSWROptions = JSON.parse(url.split('?').pop());
    return await StreamClient.getFeed<T>(feed, inkindRouteId, { ...options, ...getOptions });
  } catch (error) {
    logger.error(error);
    throw error;
  }
}

async function streamPostFetcher(url: string) {
  try {
    const pathname = url.split('?').shift();
    const parts = pathname.split('/');

    const id = parts[1];

    return await StreamClient.getPost(id);
  } catch (error) {
    logger.error(error);
    throw error;
  }
}

async function streamReactionFetcher<D>(url: string) {
  try {
    const pathname = url.split('?').shift();
    const parts = pathname.split('/');

    const id = parts[1];

    return (await StreamClient.getReaction(id)) as unknown as D;
  } catch (error) {
    logger.error(error);
    throw error;
  }
}

async function streamCommentsFetcher(url: string) {
  return streamReactionsFetcherInternal<Reaction<StreamComment>>(url, 'comment');
}

async function streamReactionsFetcher(url: string) {
  return streamReactionsFetcherInternal<Reaction<UR>>(url);
}
async function streamReactionsFetcherInternal<T>(url: string, reactionKind?: string) {
  try {
    const parentType = url.split('/').shift() as ReactionLookupAttribute;
    const parentId = url.split('/').pop().split('?').shift();
    const options: UseStreamSWROptions = JSON.parse(url.split('?').pop());
    return await StreamClient.getReactions<T>(parentType, parentId, reactionKind, options);
  } catch (error) {
    logger.error(error);
    throw error;
  }
}
